import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  Typography, 
  Button, 
  Container, 
  IconButton, 
  Menu, 
  MenuItem,
  useScrollTrigger,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Slide
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import ThemeToggle from './ThemeToggle';
import logo from '../images/company_logo.png';
import logo_dark from '../images/company_logo_dark.png';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDemoModal } from '../hooks/useDemoModal';
import { useThemeMode } from '../contexts/ThemeContext';


// Modified HideOnScroll component that uses Slide for proper animation
function HideOnScroll(props) {
  const { children, disableHide } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger || disableHide}>
      {children}
    </Slide>
  );
}

function Header(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isLight, isHydrated } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = location.pathname === '/' ? 'home' : location.pathname.substring(1);

  // All hooks must be declared before conditional returns
  const [anchorElSolutions, setAnchorElSolutions] = useState(null);
  const [anchorElPlatform, setAnchorElPlatform] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const { openModal } = useDemoModal();

  // Check if any menu is open to disable HideOnScroll
  const isAnyMenuOpen = Boolean(anchorElSolutions) || Boolean(anchorElPlatform);

  // useEffect hooks must also be at the top
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setIsScrolled(offset > 50);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Don't render until theme is properly hydrated to prevent inconsistencies
  if (!isHydrated) {
    return (
      <Box sx={{ height: '80px' }} /> // Placeholder to prevent layout shift
    );
  }
  
  // Helper function to handle navigation
  const handleNavigation = (href) => {
    if (currentPage !== 'home') {
      // If we're not on the main page, navigate to home first, then scroll to section
      navigate('/');
      setTimeout(() => {
        if (href.startsWith('#')) {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }
      }, 100);
    } else {
      // If we're already on the main page, just scroll to the section
      if (href.startsWith('#')) {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const handleOpenSolutionsMenu = (event) => {
    if (!isMobile) {
      setAnchorElSolutions(event.currentTarget);
    }
  };

  const handleCloseSolutionsMenu = () => {
    setAnchorElSolutions(null);
  };

  const handleOpenPlatformMenu = (event) => {
    if (!isMobile) {
      setAnchorElPlatform(event.currentTarget);
    }
  };

  const handleClosePlatformMenu = () => {
    setAnchorElPlatform(null);
  };

  const toggleDrawer = (open) => (event) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const toggleSolutions = () => {
    setSolutionsOpen(!solutionsOpen);
  };

  const togglePlatform = () => {
    setPlatformOpen(!platformOpen);
  };

  const navItems = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#why-alphanetix' },
    {
      label: 'Platform',
      href: '#core-features',
      hasSubmenu: true,
      submenuItems: [
        { label: 'Core Features', href: '#core-features' },
        { label: 'Workflow Demo', href: '#workflow-demo' },
        { label: 'Technologies', href: '#technologies' },
        { label: 'Deployment', href: '#deployment' },
      ]
    },
    { label: 'Industries', href: '#industry-focus' },
    { label: 'Contact', href: '#contact' }
  ];

  const drawer = (
    <Box 
      sx={{ 
        width: 320,
        maxWidth: '85vw',
        height: '100%',
        background: isLight
          ? 'linear-gradient(160deg, rgba(255,255,255,0.45) 0%, rgba(238,240,255,0.35) 50%, rgba(245,243,255,0.4) 100%)'
          : 'linear-gradient(160deg, rgba(10,14,40,0.5) 0%, rgba(6,9,28,0.45) 50%, rgba(8,12,36,0.5) 100%)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        // Glow orb — top left
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -80,
          left: -80,
          width: 260,
          height: 260,
          borderRadius: '50%',
          background: isLight
            ? 'radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(56,189,248,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
          zIndex: 0,
        },
        // Accent strip on right edge
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '2px',
          height: '100%',
          background: isLight
            ? 'linear-gradient(180deg, transparent 0%, rgba(99,102,241,0.4) 30%, rgba(129,140,248,0.3) 70%, transparent 100%)'
            : 'linear-gradient(180deg, transparent 0%, rgba(56,189,248,0.4) 30%, rgba(139,92,246,0.35) 70%, transparent 100%)',
          zIndex: 0,
        },
      }} 
      role="presentation"
    >
      {/* Top bar: Logo + Close button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 2.5,
          position: 'relative',
          zIndex: 1,
          borderBottom: isLight
            ? '1px solid rgba(99,102,241,0.1)'
            : '1px solid rgba(56,189,248,0.08)',
        }}
      >
        <Box
          component="img"
          src={isLight ? logo_dark : logo}
          alt="Alphanetix Logo"
          sx={{
            height: 32,
            filter: isLight
              ? 'brightness(0) saturate(100%) invert(24%) sepia(85%) saturate(4500%) hue-rotate(234deg) brightness(96%) contrast(100%)'
              : 'brightness(1.1) contrast(1.05)',
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ThemeToggle
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              color: isLight ? '#6366f1' : 'rgba(255, 255, 255, 0.5)',
              border: isLight
                ? '1px solid rgba(99, 102, 241, 0.15)'
                : '1px solid rgba(255, 255, 255, 0.08)',
              '&:hover': {
                color: isLight ? '#4f46e5' : '#e2e8f0',
                borderColor: isLight ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.15)',
                background: isLight
                  ? 'rgba(99, 102, 241, 0.06)'
                  : 'rgba(255, 255, 255, 0.04)',
              },
            }}
          />
          <IconButton
            onClick={toggleDrawer(false)}
            size="small"
            sx={{
              width: 36,
            height: 36,
            borderRadius: '10px',
            color: isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.4)',
            border: isLight
              ? '1px solid rgba(148, 163, 184, 0.2)'
              : '1px solid rgba(255, 255, 255, 0.08)',
            transition: 'all 0.2s ease',
            '&:hover': {
              color: isLight ? '#0f172a' : '#a5b4fc',
              borderColor: isLight ? 'rgba(0, 0, 0, 0.15)' : 'rgba(165, 180, 252, 0.2)',
              background: isLight
                ? 'rgba(0, 0, 0, 0.03)'
                : 'rgba(255, 255, 255, 0.04)',
            },
          }}
        >
            <CloseIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Navigation links */}
      <Box sx={{ flex: 1, px: 2, py: 2.5, overflowY: 'auto', position: 'relative', zIndex: 1 }}>
        <List disablePadding>
          {navItems.map((item, index) => (
            <React.Fragment key={index}>
              {item.hasSubmenu ? (
                <>
                  <ListItem
                    button
                    onClick={item.label === 'Solutions' ? toggleSolutions : togglePlatform}
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderRadius: '12px',
                      mb: 0.25,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: isLight
                          ? 'rgba(0, 0, 0, 0.03)'
                          : 'rgba(255, 255, 255, 0.04)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontFamily: 'Montserrat',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: isLight ? '#1e293b' : '#e2e8f0',
                        letterSpacing: '-0.01em',
                      }}
                    />
                    {(item.label === 'Solutions' ? solutionsOpen : platformOpen) ? (
                      <ExpandLess sx={{ color: isLight ? '#0f172a' : '#10b981', fontSize: '1.2rem', transition: 'transform 0.2s ease' }} />
                    ) : (
                      <ExpandMore sx={{ color: isLight ? '#cbd5e1' : '#475569', fontSize: '1.2rem', transition: 'transform 0.2s ease' }} />
                    )}
                  </ListItem>
                  <Collapse in={item.label === 'Solutions' ? solutionsOpen : platformOpen} timeout={250} unmountOnExit>
                    <List component="div" disablePadding sx={{ ml: 1.5, my: 0.5 }}>
                      {item.submenuItems.map((subItem, subIndex) => (
                        <ListItem
                          button
                          key={subIndex}
                          onClick={(e) => {
                            e.preventDefault();
                            toggleDrawer(false)();
                            handleNavigation(subItem.href);
                          }}
                          sx={{
                            px: 1.5,
                            py: 0.875,
                            borderRadius: '10px',
                            mb: 0.25,
                            borderLeft: isLight
                              ? '2px solid rgba(0, 0, 0, 0.05)'
                              : '2px solid rgba(165, 180, 252, 0.08)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              background: isLight
                                ? 'rgba(0, 0, 0, 0.02)'
                                : 'rgba(255, 255, 255, 0.03)',
                              borderLeftColor: isLight ? '#0f172a' : '#a5b4fc',
                              pl: 2,
                            },
                          }}
                        >
                          <ListItemText
                            primary={subItem.label}
                            primaryTypographyProps={{
                              fontFamily: 'Montserrat',
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              color: isLight ? '#64748b' : '#94a3b8',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                <ListItem
                  button
                  onClick={(e) => {
                    e.preventDefault();
                    toggleDrawer(false)();
                    handleNavigation(item.href);
                  }}
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    borderRadius: '12px',
                    mb: 0.25,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: isLight
                        ? 'rgba(0, 0, 0, 0.03)'
                        : 'rgba(255, 255, 255, 0.04)',
                    },
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontFamily: 'Montserrat',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      color: isLight ? '#1e293b' : '#e2e8f0',
                      letterSpacing: '-0.01em',
                    }}
                  />
                </ListItem>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Bottom CTA buttons */}
      <Box
        sx={{
          px: 2.5,
          pb: 3.5,
          pt: 2,
          position: 'relative',
          zIndex: 1,
          borderTop: isLight
            ? '1px solid rgba(99,102,241,0.1)'
            : '1px solid rgba(56,189,248,0.08)',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        <Button
          variant="contained"
          fullWidth
          onClick={openModal}
          disableElevation
          sx={{
            py: 1.25,
            fontWeight: 700,
            fontSize: '0.85rem',
            textTransform: 'none',
            letterSpacing: '0.01em',
            borderRadius: '12px',
            background: isLight
              ? 'linear-gradient(135deg, #4338ca 0%, #4f46e5 50%, #6366f1 100%)'
              : 'linear-gradient(135deg, #38bdf8 0%, #8b5cf6 100%)',
            color: '#ffffff',
            boxShadow: isLight
              ? '0 4px 14px rgba(79, 70, 229, 0.25)'
              : '0 4px 14px rgba(56, 189, 248, 0.25)',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: isLight
                ? 'linear-gradient(135deg, #3730a3 0%, #4338ca 50%, #4f46e5 100%)'
                : 'linear-gradient(135deg, #0ea5e9 0%, #7c3aed 100%)',
              boxShadow: isLight
                ? '0 6px 20px rgba(79, 70, 229, 0.35)'
                : '0 6px 20px rgba(56, 189, 248, 0.35)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          Request a Demo
        </Button>
      </Box>
    </Box>
  );


  return (
    <>
      <HideOnScroll disableHide={isAnyMenuOpen} {...props}>
        <AppBar
          position="fixed"
          color="transparent"
          elevation={0}
          sx={{
            background: isScrolled 
              ? (isLight 
                  ? 'rgba(255, 255, 255, 0.65)'
                  : 'linear-gradient(40deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 70%, #0f172a 90%, #020617 100%)')
              : 'transparent',
            backgroundColor: 'transparent',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            backdropFilter: isScrolled ? (isLight ? 'blur(24px) saturate(180%)' : 'blur(20px)') : 'none',
            WebkitBackdropFilter: isScrolled ? (isLight ? 'blur(24px) saturate(180%)' : 'blur(20px)') : 'none',
            py: { xs: 0, md: isScrolled ? 0.8 : 1.2 },
            boxShadow: isScrolled 
              ? (isLight 
                  ? '0 8px 32px rgba(99, 102, 241, 0.06), 0 1px 0 rgba(255, 255, 255, 0.5)'
                  : '0 8px 32px rgba(0, 0, 0, 0.3), 0 1px 0 rgba(16, 185, 129, 0.2)')
              : 'none',
            border: isScrolled 
              ? (isLight 
                  ? '1px solid rgba(255, 255, 255, 0.6)' 
                  : 'none') 
              : 'none',
            borderTop: 'none',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'transparent',
              zIndex: -1,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: isLight
                ? 'linear-gradient(90deg, transparent 0%, rgba(99, 102, 241, 0.15) 20%, rgba(99, 102, 241, 0.1) 50%, rgba(99, 102, 241, 0.05) 80%, transparent 100%)'
                : 'linear-gradient(90deg, transparent 0%, rgba(139, 92, 246, 0.4) 20%, rgba(34, 197, 94, 0.3) 50%, rgba(239, 68, 68, 0.2) 80%, transparent 100%)',
              opacity: isScrolled ? 1 : 0,
              transition: 'opacity 0.3s ease',
            }
          }}
        >
          <Container maxWidth="xl" sx={{ px: { md: 2, lg: 3 } }}>
            <Toolbar 
              disableGutters
              sx={{ 
                minHeight: { xs: '56px', md: '64px' }, // Fixed height to prevent layout shift
                height: { xs: '56px', md: '64px' }
              }}
            >
              {/* Logo for desktop */}
              <Box
                component="div"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
                sx={{
                  mr: 5,
                  display: { xs: 'none', md: 'flex' },
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                <Box
                  component="img"
                  src={isLight ? logo_dark : logo}
                  alt="Alphanetix Logo"
                  sx={{
                    height: { md: isScrolled ? '40px' : '50px' },
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    filter: isLight
                      ? 'brightness(0) saturate(100%) invert(24%) sepia(85%) saturate(4500%) hue-rotate(234deg) brightness(96%) contrast(100%)'
                      : 'brightness(1.1) contrast(1.05)',
                    '&:hover': {
                      filter: isLight
                        ? 'brightness(0) saturate(100%) invert(30%) sepia(90%) saturate(5000%) hue-rotate(234deg) brightness(100%) contrast(100%)'
                        : 'brightness(1.2) contrast(1.1)',
                      transform: 'scale(1.02)'
                    }
                  }}
                />
              </Box>

              {/* Mobile menu icon */}
              <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                <IconButton
                  size="large"
                  aria-label="menu"
                  onClick={toggleDrawer(true)}
                  sx={{
                    color: isLight ? '#0f172a' : '#ffffff',
                    background: isLight
                      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.6) 0%, rgba(99, 102, 241, 0.05) 100%)'
                      : 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(34, 197, 94, 0.1) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: isLight
                      ? '1px solid rgba(99, 102, 241, 0.12)'
                      : '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: 1,
                    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                      background: isLight
                        ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(129, 140, 248, 0.05) 100%)'
                        : 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(34, 197, 94, 0.15) 100%)',
                      transform: 'scale(1.05)',
                      boxShadow: isLight
                        ? '0 4px 20px rgba(99, 102, 241, 0.15)'
                        : '0 4px 20px rgba(139, 92, 246, 0.3)'
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>

              {/* Logo for mobile */}
              <Box
                component="div"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
                sx={{
                  flexGrow: 1,
                  display: { xs: 'flex', md: 'none' },
                  justifyContent: 'center',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                <Box
                  component="img"
                  src={isLight ? logo_dark : logo}
                  alt="Alphanetix Logo"
                  sx={{
                    height: { xs: isScrolled ? '32px' : '40px' },
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    filter: isLight
                      ? 'brightness(0) saturate(100%) invert(24%) sepia(95%) saturate(4839%) hue-rotate(234deg) brightness(96%) contrast(93%)'
                      : 'brightness(1.1) contrast(1.05)',
                    '&:hover': {
                      filter: isLight
                        ? 'brightness(0) saturate(100%) invert(30%) sepia(95%) saturate(4839%) hue-rotate(234deg) brightness(100%) contrast(93%)'
                        : 'brightness(1.2) contrast(1.1)',
                      transform: 'scale(1.02)'
                    }
                  }}
                />
              </Box>

              {/* Desktop navigation */}
              <Box sx={{ 
                flexGrow: 1, 
                display: { xs: 'none', md: 'flex' }, 
                justifyContent: 'center', 
                alignItems: 'center',
                flexWrap: 'nowrap',
                overflow: 'hidden'
              }}>
                {navItems.map((item, index) => (
                  <React.Fragment key={index}>
                    {item.hasSubmenu ? (
                      <>
                        <Button
                          onClick={item.label === 'Solutions' ? handleOpenSolutionsMenu : handleOpenPlatformMenu}
                          endIcon={<KeyboardArrowDownIcon />}
                          sx={{
                            mx: 0.5,
                            color: isLight ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                            fontFamily: 'Montserrat',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            letterSpacing: '0.3px',
                            textTransform: 'uppercase',
                            position: 'relative',
                            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                            borderRadius: 1,
                            px: 1.2,
                            py: 1,
                            whiteSpace: 'nowrap',
                            minWidth: 'auto',
                            height: '40px', // Fixed height to prevent layout shift
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: isLight
                                ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(129, 140, 248, 0.03) 100%)'
                                : 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(34, 197, 94, 0.08) 100%)',
                              opacity: 0,
                              transition: 'opacity 0.3s ease',
                              borderRadius: 1,
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              bottom: 0,
                              left: '50%',
                              width: '0%',
                              height: '2px',
                              background: isLight 
                                ? 'linear-gradient(90deg, #8b5cf6, #38bdf8)'
                                : 'linear-gradient(90deg, #38bdf8, #8b5cf6)',
                              transform: 'translateX(-50%)',
                              transition: 'width 0.3s ease',
                            },
                            '&:hover': {
                              color: isLight ? '#818cf8' : '#38bdf8',
                              '&::before': {
                                opacity: 1,
                              },
                              '&::after': {
                                width: '80%'
                              }
                            }
                          }}
                        >
                          {item.label}
                        </Button>
                        <Menu
                          id={`${item.label.toLowerCase()}-menu`}
                          anchorEl={item.label === 'Solutions' ? anchorElSolutions : anchorElPlatform}
                          open={Boolean(item.label === 'Solutions' ? anchorElSolutions : anchorElPlatform)}
                          onClose={item.label === 'Solutions' ? handleCloseSolutionsMenu : handleClosePlatformMenu}
                          MenuListProps={{
                            'aria-labelledby': `${item.label.toLowerCase()}-button`,
                          }}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                          }}
                          sx={{
                            zIndex: 1300,
                            '& .MuiMenu-paper': {
                              position: 'fixed', // Use fixed positioning to prevent layout shifts
                            }
                          }}
                          PaperProps={{
                            elevation: 0,
                            sx: {
                              mt: 1.5,
                              minWidth: 200,
                              borderRadius: 3,
                              overflow: 'visible',
                              background: isLight
                                ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.5), rgba(248, 250, 252, 0.4))'
                                : 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 58, 138, 0.9))',
                              backdropFilter: 'blur(20px)',
                              border: isLight 
                                ? '1px solid rgba(99, 102, 241, 0.2)'
                                : '1px solid rgba(139, 92, 246, 0.2)',
                              boxShadow: isLight
                                ? '0 10px 40px rgba(99, 102, 241, 0.15)'
                                : '0 10px 40px rgba(0, 0, 0, 0.3)',
                              '&:before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                left: '50%',
                                width: 10,
                                height: 10,
                                background: isLight
                                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.5), rgba(248, 250, 252, 0.4))'
                                  : 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 58, 138, 0.9))',
                                border: isLight 
                                  ? '1px solid rgba(99, 102, 241, 0.2)'
                                  : '1px solid rgba(139, 92, 246, 0.2)',
                                transform: 'translate(-50%, -50%) rotate(45deg)',
                                zIndex: 0,
                              },
                            }
                          }}
                        >
                          {item.submenuItems.map((subItem, subIndex) => (
                            <MenuItem
                              key={subIndex}
                              onClick={(e) => {
                                e.preventDefault();
                                item.label === 'Solutions' ? handleCloseSolutionsMenu() : handleClosePlatformMenu();
                                handleNavigation(subItem.href);
                              }}
                              sx={{ 
                                fontFamily: 'Montserrat', 
                                fontWeight: 600,
                                color: isLight ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                                fontSize: '0.9rem',
                                py: 1.5,
                                px: 2.5,
                                transition: 'all 0.3s ease',
                                borderRadius: 1,
                                mx: 1,
                                my: 0.5,
                                '&:hover': {
                                  backgroundColor: isLight 
                                    ? 'rgba(99, 102, 241, 0.1)' 
                                    : 'rgba(139, 92, 246, 0.2)',
                                  color: isLight ? '#818cf8' : '#22c55e',
                                  transform: 'translateX(8px)'
                                }
                              }}
                            >
                              {subItem.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    ) : (
                      <Button
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavigation(item.href);
                        }}
                        sx={{
                          mx: 0.5,
                          color: isLight ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                          fontFamily: 'Montserrat',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          letterSpacing: '0.3px',
                          textTransform: 'uppercase',
                          position: 'relative',
                          transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                          borderRadius: 1,
                          px: 1.2,
                          py: 1,
                          whiteSpace: 'nowrap',
                          minWidth: 'auto',
                          height: '40px', // Fixed height to prevent layout shift
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: isLight
                              ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(5, 150, 105, 0.03) 100%)'
                              : 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(34, 197, 94, 0.08) 100%)',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                            borderRadius: 1,
                          },
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            width: '0%',
                            height: '2px',
                            background: isLight 
                              ? 'linear-gradient(90deg, #8b5cf6, #38bdf8)'
                              : 'linear-gradient(90deg, #38bdf8, #8b5cf6)',
                            transform: 'translateX(-50%)',
                            transition: 'width 0.3s ease',
                          },
                          '&:hover': {
                            color: isLight ? '#0f172a' : '#059669',
                            '&::before': {
                              opacity: 1,
                            },
                            '&::after': {
                              width: '80%'
                            }
                          }
                        }}
                      >
                        {item.label}
                      </Button>
                    )}
                  </React.Fragment>
                ))}
              </Box>

              {/* CTA Buttons - Desktop */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                <ThemeToggle
                  sx={{
                    color: isLight ? '#6366f1' : 'rgba(129, 140, 248, 0.8)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: isLight ? '#4f46e5' : '#a5b4fc',
                      background: isLight
                        ? 'rgba(99, 102, 241, 0.08)'
                        : 'rgba(129, 140, 248, 0.12)',
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={openModal}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    borderRadius: 1,
                    px: 3,
                    py: 1.5,
                    whiteSpace: 'nowrap',
                    background: isLight 
                      ? 'linear-gradient(135deg, #4338ca 0%, #4f46e5 50%, #6366f1 100%)'
                      : 'linear-gradient(135deg, #38bdf8 0%, #8b5cf6 100%)',
                    color: '#ffffff',
                    border: isLight ? '1px solid rgba(255,255,255,0.15)' : 'none',
                    boxShadow: isLight
                      ? '0 4px 20px rgba(79, 70, 229, 0.25), inset 0 1px 0 rgba(255,255,255,0.1)'
                      : '0 4px 20px rgba(56, 189, 248, 0.3)',
                    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    position: 'relative',
                    overflow: 'hidden',
                    backdropFilter: 'blur(20px)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                      transition: 'left 0.5s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-3px) scale(1.05)',
                      background: isLight
                        ? 'linear-gradient(135deg, #3730a3 0%, #4338ca 50%, #4f46e5 100%)'
                        : 'linear-gradient(135deg, #0ea5e9 0%, #7c3aed 100%)',
                      boxShadow: isLight
                        ? '0 8px 30px rgba(79, 70, 229, 0.35)'
                        : '0 8px 30px rgba(56, 189, 248, 0.5)',
                      '&::before': {
                        left: '100%',
                      }
                    }
                  }}
                >
                  Request a Demo
                </Button>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>

      {/* Mobile navigation drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        disableScrollLock={true}
        PaperProps={{
          sx: {
            top: { xs: '56px', md: '64px' },
            height: { xs: 'calc(100% - 56px)', md: 'calc(100% - 64px)' },
            background: isLight
              ? 'rgba(245, 246, 255, 0.6)'
              : 'rgba(6, 9, 28, 0.55)',
            backdropFilter: 'blur(32px) saturate(160%)',
            WebkitBackdropFilter: 'blur(32px) saturate(160%)',
            boxShadow: isLight
              ? '8px 0 40px rgba(99,102,241,0.1), 1px 0 0 rgba(99,102,241,0.08)'
              : '8px 0 48px rgba(0,0,0,0.5), 1px 0 0 rgba(56,189,248,0.08)',
            border: 'none',
            overflow: 'hidden',
          },
        }}
        transitionDuration={300}
        SlideProps={{ easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
      >
        {drawer}
      </Drawer>
    </>
  );
}

export default Header;
